<template>
  <div v-if="loading" class="loading">
    <SyncLoader :size="15" margin="5px" :color="config.titleColor" />
  </div>
  <div v-else class="chatbot">
    <ChatHeader />
    <div
      class="chatbot-body"
      ref="chatbotBodyScroll"
      v-on:scroll.passive="onScroll"
    >
      <template v-for="(message, index) in orderedHistory">
        <StickyDate
          v-if="message.id in firstMessagesByDay"
          :key="firstMessagesByDay[message.id]"
          :date="message.datetime"
        />
        <Message
          :key="message.id"
          :received="message.received"
          :content="message.content"
          :datetime="message.datetime"
          :fixedWidth="
            index == orderedHistory.length - 1 && showVerticalChoices
          "
        />
      </template>
      <Message
        v-for="message in sendingMessages"
        :key="message.tempId"
        :received="false"
        :content="message.content"
        :datetime="message.datetime"
        :sending="true"
        :sendingError="message.hasError"
      />
      <AnswerChoicesVertical v-if="showVerticalChoices" />
    </div>
    <AnswerChoicesHorizontal v-if="showHorizontalChoices" />
    <footer>
      <AnswerText v-if="showInputAnswer" />
    </footer>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { SyncLoader } from "@saeris/vue-spinners";

import ChatHeader from "../components/ChatHeader.vue";
import Message from "../components/Message.vue";
import StickyDate from "../components/StickyDate.vue";

const interval = process.env.VUE_APP_CHATBOT_LOAD_MESSAGES_MS_INTERVAL || 5000;

export default {
  name: "Chat",
  props: {},
  data: () => ({
    firstScroll: false,
    loadNewMessagesInterval: null
  }),
  components: {
    ChatHeader,
    Message,
    StickyDate,
    AnswerText: () =>
      import(/* webpackChunkName: "Answers" */ "../components/AnswerText.vue"),
    AnswerChoicesHorizontal: () =>
      import(
        /* webpackChunkName: "Answers" */ "../components/AnswerChoicesHorizontal.vue"
      ),
    AnswerChoicesVertical: () =>
      import(
        /* webpackChunkName: "Answers" */ "../components/AnswerChoicesVertical.vue"
      ),
    SyncLoader
  },
  computed: {
    ...mapState("settings", ["loading", "config"]),
    ...mapState("messages", ["showInputAnswer", "sendingMessages"]),
    ...mapGetters("messages", [
      "orderedHistory",
      "firstMessagesByDay",
      "showHorizontalChoices",
      "showVerticalChoices"
    ]),
    mustScroll() {
      return [
        this.showInputAnswer,
        this.sendingMessages,
        this.showHorizontalChoices,
        this.showVerticalChoices,
        this.orderedHistory,
        this.loading
      ];
    }
  },
  methods: {
    ...mapActions("messages", ["onScroll"]),
    scrollToBottom() {
      const el = this.$refs["chatbotBodyScroll"];
      if (!el) return;
      if (
        this.firstScroll &&
        el.scrollTop + el.clientHeight < el.scrollHeight - 150
      ) {
        // do not scroll if scrolled up mannually
        return;
      }

      if (typeof el.scroll === "function") {
        el.scroll({
          top: el.scrollHeight,
          behavior: this.firstScroll ? "smooth" : "instant"
        });
      } else {
        el.scrollTop = el.scrollHeight;
      }
      this.firstScroll = true;
    }
  },
  watch: {
    mustScroll: {
      handler: function() {
        setTimeout((() => this.scrollToBottom()).bind(this), 500);
      },
      deep: true
    }
  },
  beforeCreate() {
    this.$store.dispatch("settings/loadConfig");
  },
  created() {
    this.$store.dispatch("messages/loadNewMessages");
    this.loadNewMessagesInterval = setInterval(
      (() => this.$store.dispatch("messages/loadNewMessages")).bind(this),
      interval
    );
  },
  mounted() {
    setTimeout((() => this.scrollToBottom()).bind(this), 500);
  },
  beforeDestroy() {
    clearInterval(this.loadNewMessagesInterval);
  }
};
</script>

<style lang="scss" scoped>
.loading {
  margin: 20% auto 0;
  text-align: center;
}

.chatbot {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  .chatbot-body {
    flex: 1 1;
    padding: 0 10px 10px;
    overflow-y: auto;
  }
}
</style>
